import { Command } from "commander";
import chalk from "chalk";
import fs from "fs";
import path from "path";
import { runAnalysis } from "../analyze/analyzer.js";
import { AnalysisResult, CompatibilityIssue } from "../analyze/models.js";
import { scoreLabel } from "../analyze/healthScorer.js";
import { loadConfig } from "../config/configLoader.js";
import { evaluateThresholds } from "../ci/ciThresholdEvaluator.js";
import { generateCiReport, sortIssues } from "../ci/ciReportGenerator.js";
import { buildSarifReport, writeSarifReport } from "../ci/sarifExporter.js";
import {
  createBaseline,
  writeBaseline,
  loadBaseline,
  filterBaselineIssues,
} from "../ci/baselineManager.js";
import { resolveExitCode } from "../ci/exitCodeResolver.js";
import { findPackageJson } from "../utils/files.js";

// ─── Helpers ────────────────────────────────────────────────────────────────

function severityIcon(issue: CompatibilityIssue, ci: boolean): string {
  if (ci) {
    switch (issue.severity) {
      case "error":
        return "[ERROR]";
      case "warning":
        return "[WARN]";
      case "info":
      default:
        return "[INFO]";
    }
  }
  switch (issue.severity) {
    case "error":
      return chalk.red("✖");
    case "warning":
      return chalk.yellow("⚠");
    case "info":
    default:
      return chalk.cyan("ℹ");
  }
}

function printIssues(
  label: string,
  issues: CompatibilityIssue[],
  color: chalk.Chalk,
  ci: boolean
): void {
  if (issues.length === 0) return;

  if (ci) {
    console.log(`\n${label.toUpperCase()}`);
    for (const issue of issues) {
      console.log(`  ${severityIcon(issue, ci)} [${issue.packageName}] ${issue.message}`);
      if (issue.recommendation) {
        console.log(`    -> ${issue.recommendation}`);
      }
    }
  } else {
    console.log(color.bold(label));
    for (const issue of issues) {
      console.log(`  ${severityIcon(issue, ci)} [${issue.packageName}] ${issue.message}`);
      if (issue.recommendation) {
        console.log(`    ${chalk.dim("→")} ${chalk.dim(issue.recommendation)}`);
      }
    }
    console.log();
  }
}

/** Write CiReport JSON to a file, creating parent dirs as needed. */
function writeJsonOutput(outputPath: string, data: object): void {
  const dir = path.dirname(path.resolve(outputPath));
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }
  fs.writeFileSync(path.resolve(outputPath), JSON.stringify(data, null, 2), "utf-8");
}

// ─── Command ────────────────────────────────────────────────────────────────

interface AnalyzeOptions {
  json?: boolean;
  verbose?: boolean;
  ci?: boolean;
  jsonOutput?: string;
  sarif?: boolean;
  baseline?: boolean;
}

export const analyzeCommand = new Command("analyze")
  .description("Perform deep React Native project analysis and compatibility validation")
  .option("--json", "Output analysis results in JSON format (stdout)")
  .option("--verbose", "Show detailed output including native module names and plugin info")
  .option("--ci", "CI mode: plain output, no colors, proper exit codes, reads config thresholds")
  .option("--json-output <file>", "Write CiReport JSON to a file")
  .option("--sarif", "Generate a SARIF v2.1.0 report (kyro-analysis.sarif)")
  .option("--baseline", "Generate a baseline snapshot (.kyro/baseline.json)")
  .action(async (options: AnalyzeOptions) => {
    const cwd = process.cwd();
    const ci = !!options.ci;

    // Resolve project root early (needed for config + baseline)
    const packageJsonPath = findPackageJson(cwd);
    const projectRoot = packageJsonPath ? path.dirname(packageJsonPath) : cwd;

    // ── Handle internal errors ──────────────────────────────────────────────
    try {
      // 1. Load config
      let config = loadConfig(projectRoot);

      // 2. Run analysis
      let result: AnalysisResult;
      try {
        result = await runAnalysis(cwd);
      } catch (err: any) {
        const message = err?.message || "Unknown error";
        const isConfigError = message.includes("package.json");

        if (options.json || options.jsonOutput) {
          const payload = { success: false, error: message };
          if (options.json) console.log(JSON.stringify(payload, null, 2));
          if (options.jsonOutput) writeJsonOutput(options.jsonOutput, payload);
        } else if (ci) {
          console.error(`[FATAL] Analysis failed: ${message}`);
        } else {
          console.log();
          console.log(chalk.red(`✖ Analysis failed: ${message}`));
          console.log();
        }

        process.exit(
          resolveExitCode(isConfigError ? "invalid_config" : "internal_error")
        );
        return;
      }

      // ── Baseline mode: generate snapshot & exit ─────────────────────────
      if (options.baseline) {
        const allIssues: CompatibilityIssue[] = [
          ...result.errors,
          ...result.warnings,
          ...result.info,
        ];
        const baseline = createBaseline(allIssues);
        writeBaseline(projectRoot, baseline);

        if (ci) {
          console.log(`[INFO] Baseline written with ${baseline.issueIds.length} issue(s).`);
          console.log(`[INFO] Path: ${path.join(projectRoot, ".kyro", "baseline.json")}`);
        } else {
          console.log();
          console.log(chalk.green("✓") + chalk.bold(" Baseline generated"));
          console.log(
            `  ${chalk.dim("→")} ${path.join(projectRoot, ".kyro", "baseline.json")}`
          );
          console.log(`  ${chalk.dim("→")} ${baseline.issueIds.length} issue(s) captured`);
          console.log();
        }
        process.exit(resolveExitCode("success"));
        return;
      }

      // ── Baseline filtering (CI runs only) ───────────────────────────────
      let filteredResult = result;
      if (ci) {
        const baseline = loadBaseline(projectRoot);
        if (baseline) {
          const newErrors = filterBaselineIssues(result.errors, baseline);
          const newWarnings = filterBaselineIssues(result.warnings, baseline);
          const newInfo = filterBaselineIssues(result.info, baseline);

          // Reconstruct filtered result with recalculated score
          const filteredIssues = [...newErrors, ...newWarnings, ...newInfo];
          const { computeHealthScore, partitionIssues } = await import(
            "../analyze/healthScorer.js"
          );
          const newScore = computeHealthScore(filteredIssues, result.nativeModules);
          const partitioned = partitionIssues(filteredIssues);

          filteredResult = {
            ...result,
            score: newScore,
            errors: partitioned.errors,
            warnings: partitioned.warnings,
            info: partitioned.info,
          };

          if (ci && baseline.issueIds.length > 0) {
            const suppressed =
              result.errors.length +
              result.warnings.length +
              result.info.length -
              filteredIssues.length;
            if (suppressed > 0) {
              console.log(
                `[INFO] Baseline active: ${suppressed} pre-existing issue(s) suppressed.`
              );
            }
          }
        }
      }

      // 3. Evaluate thresholds
      const evaluation = evaluateThresholds(filteredResult, config);

      // 4. Generate CI report (used for --json-output and --sarif)
      const ciReport = generateCiReport(filteredResult, evaluation);

      // ── SARIF export ────────────────────────────────────────────────────
      if (options.sarif) {
        const sarifReport = buildSarifReport(ciReport);
        const sarifPath = path.join(cwd, "kyro-analysis.sarif");
        writeSarifReport(sarifReport, sarifPath);

        if (ci) {
          console.log(`[INFO] SARIF report written to: ${sarifPath}`);
        } else {
          console.log();
          console.log(chalk.green("✓") + chalk.bold(" SARIF report generated"));
          console.log(`  ${chalk.dim("→")} ${sarifPath}`);
          console.log();
        }
      }

      // ── JSON file output ─────────────────────────────────────────────────
      if (options.jsonOutput) {
        writeJsonOutput(options.jsonOutput, ciReport);
        if (!ci) {
          console.log();
          console.log(chalk.green("✓") + chalk.bold(` Report saved to ${options.jsonOutput}`));
        }
      }

      // ── Stdout JSON mode ────────────────────────────────────────────────
      if (options.json) {
        console.log(JSON.stringify(ciReport, null, 2));
        process.exit(
          resolveExitCode(
            evaluation.outcome === "PASS"
              ? "success"
              : evaluation.outcome === "WARNING_LIMIT"
              ? "warning_limit"
              : "critical_errors"
          )
        );
        return;
      }

      // ── CI plain-text output ────────────────────────────────────────────
      if (ci) {
        console.log("=== React Native Project Analysis ===");
        console.log(`status:       ${ciReport.status.toUpperCase()}`);
        console.log(`score:        ${ciReport.score}/100`);
        console.log(`errors:       ${ciReport.errors}`);
        console.log(`warnings:     ${ciReport.warnings}`);
        console.log(`infos:        ${ciReport.infos}`);
        console.log(`issueCount:   ${ciReport.issueCount}`);
        console.log(`reason:       ${evaluation.reason}`);
        console.log(`generatedAt:  ${ciReport.generatedAt}`);

        const sorted = sortIssues([
          ...filteredResult.errors,
          ...filteredResult.warnings,
          ...filteredResult.info,
        ]);

        if (sorted.length > 0) {
          console.log("\n--- Issues ---");
          for (const issue of sorted) {
            console.log(
              `${severityIcon(issue, true)} [${issue.packageName}] ${issue.message}`
            );
            if (issue.recommendation) {
              console.log(`  -> ${issue.recommendation}`);
            }
          }
        }

        console.log("\n=== End of Report ===");

        const exitReason =
          evaluation.outcome === "PASS"
            ? "success"
            : evaluation.outcome === "WARNING_LIMIT"
            ? "warning_limit"
            : "critical_errors";

        process.exit(resolveExitCode(exitReason));
        return;
      }

      // ── Human-readable output (default) ────────────────────────────────
      console.log();
      console.log(chalk.bold.underline("React Native Project Analysis"));
      console.log();

      console.log(chalk.bold("Overview"));
      console.log(
        `  React Native     ${
          result.reactNativeVersion === "unknown"
            ? chalk.red("Not found")
            : chalk.cyan(result.reactNativeVersion)
        }`
      );
      console.log(`  Dependencies     ${chalk.cyan(result.dependencies)}`);
      console.log(`  Native Modules   ${chalk.cyan(result.nativeModules)}`);
      console.log(`  Plugins Loaded   ${chalk.cyan(result.plugins)}`);
      console.log();

      if (options.verbose) {
        if (result.nativeModuleNames.length > 0) {
          console.log(chalk.bold("Native Modules"));
          for (const mod of result.nativeModuleNames) {
            console.log(`  ${chalk.dim("·")} ${mod}`);
          }
          console.log();
        }

        if (result.pluginNames.length > 0) {
          console.log(chalk.bold("Active Plugins"));
          for (const name of result.pluginNames) {
            console.log(`  ${chalk.green("✓")} ${name}`);
          }
          console.log();
        }
      }

      // Issues (human)
      printIssues("Errors", filteredResult.errors, chalk.red, false);
      printIssues("Warnings", filteredResult.warnings, chalk.yellow, false);
      printIssues("Info", filteredResult.info, chalk.cyan, false);

      // Health score
      const label = scoreLabel(filteredResult.score);
      const scoreColor =
        filteredResult.score >= 90
          ? chalk.green
          : filteredResult.score >= 70
          ? chalk.cyan
          : filteredResult.score >= 50
          ? chalk.yellow
          : chalk.red;

      console.log(chalk.bold("Health Score"));
      console.log(`  ${scoreColor(`${filteredResult.score}/100`)}  ${chalk.dim(label)}`);
      console.log();

      // Threshold outcome for human output
      if (evaluation.outcome !== "PASS") {
        console.log(chalk.yellow.bold(`⚠ Quality Gate: ${evaluation.outcome}`));
        console.log(`  ${chalk.dim(evaluation.reason)}`);
        console.log();
      }

      const exitReason =
        evaluation.outcome === "PASS"
          ? "success"
          : evaluation.outcome === "WARNING_LIMIT"
          ? "warning_limit"
          : "critical_errors";

      process.exit(resolveExitCode(exitReason));
    } catch (err: any) {
      const message = err?.message || "Unknown error";
      if (ci) {
        console.error(`[FATAL] Internal error: ${message}`);
      } else {
        console.log();
        console.log(chalk.red(`✖ Internal error: ${message}`));
        console.log();
      }
      process.exit(resolveExitCode("internal_error"));
    }
  });
